import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Platform,
  Dimensions,
  ActivityIndicator,
  TouchableOpacity,
  Pressable,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import {
  ADOPTION_WIDGETS,
  adoptionDashboardUrl,
  widgetDisplayValue,
  widgetNumericValue,
} from '../utils/adoptionApi';
import { hasSuperUserAccess } from '../utils/sessionUser';

function useWindowWidth() {
  const [w, setW] = useState(Dimensions.get('window').width);
  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => setW(window.width));
    return () => sub?.remove?.();
  }, []);
  return w;
}

export default function AdoptionPage({ navigation, route }) {
  const theme = useTheme();
  const isDark = theme.isDarkMode;
  const stopwatchRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const width = useWindowWidth();

  const isDesktop = width >= 1024;
  const isTablet = width >= 640 && width < 1024;

  const gridItemStyle = useMemo(
    () => ({
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: isDesktop ? '31%' : isTablet ? '47%' : '100%',
      minWidth: isDesktop ? 280 : isTablet ? 260 : '100%',
      maxWidth: '100%',
      padding: 8,
    }),
    [isDesktop, isTablet]
  );

  useEffect(() => {
    const u = route?.params?.user;
    if (u) setCurrentUser(u);
    if (u && !hasSuperUserAccess(u)) {
      navigation.replace('MainPage', { user: u });
    }
  }, [route?.params?.user, navigation]);

  const load = useCallback(async () => {
    const u = currentUser || route?.params?.user;
    if (!u?.id || !hasSuperUserAccess(u)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(adoptionDashboardUrl(u.id));
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setDashboard(json.dashboard || null);
    } catch (e) {
      setError(e.message || 'Failed to load adoption dashboard');
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [currentUser, route?.params?.user]);

  useEffect(() => {
    void load();
  }, [load]);

  const openReport = (reportKey) => {
    const u = currentUser || route?.params?.user;
    navigation.navigate('AdoptionReports', {
      reportKey,
      userId: u?.id,
      user: u,
    });
  };

  const pageBg = isDark ? '#181818' : '#f3f2f2';
  const cardBg = isDark ? theme.colors.surface : '#ffffff';
  const cardBorder = isDark ? '#3e3e3c' : '#dddbda';
  const metricColor = isDark ? '#fdfd0a' : '#0176d3';

  const renderWidget = (widget) => {
    const value = widgetDisplayValue(widget.key, dashboard);
    const count = widgetNumericValue(widget.key, dashboard);
    const isEmpty = count === 0 && widget.valueType === 'count';
    const webLinkStyle = Platform.OS === 'web' ? { cursor: 'pointer' } : null;

    return (
      <View style={gridItemStyle} key={widget.key}>
        <Pressable
          onPress={() => openReport(widget.key)}
          style={({ pressed }) => [
            styles.widgetCard,
            { backgroundColor: cardBg, borderColor: cardBorder },
            pressed && { opacity: 0.92 },
            webLinkStyle,
          ]}
          {...(Platform.OS === 'web' ? { dataSet: { adoptionWidget: widget.key } } : {})}
        >
          <View style={styles.widgetHead}>
            <Text style={[styles.widgetTitle, { color: theme.colors.heading }]}>
              {widget.title}
            </Text>
            <Text style={[styles.widgetSub, { color: theme.colors.text }]} numberOfLines={2}>
              {widget.subtitle}
            </Text>
          </View>
          <Text
            style={[
              widget.valueType === 'names' ? styles.widgetNames : styles.widgetMetric,
              { color: isEmpty ? (isDark ? '#706e6b' : '#999') : metricColor },
            ]}
            numberOfLines={widget.valueType === 'names' ? 4 : 1}
          >
            {loading ? '…' : value}
          </Text>
          <Text style={[styles.widgetLinkHint, { color: '#0176d3' }]}>View report →</Text>
        </Pressable>
      </View>
    );
  };

  if (!hasSuperUserAccess(currentUser) && !hasSuperUserAccess(route?.params?.user)) {
    return null;
  }

  return (
    <View
      style={[styles.container, { backgroundColor: pageBg }]}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'adoption' } } : {})}
    >
      <StatusBar style={isDark ? 'light' : 'dark'} />
      <View style={[styles.header, { backgroundColor: isDark ? theme.colors.background : '#fff' }]}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={[styles.breadcrumb, { color: theme.colors.heading }]}>Adoption</Text>
          <Text style={[styles.breadcrumbSub, { color: theme.colors.text }]}>
            Content readiness · Stand out
          </Text>
        </View>
        <ProfileIcon navigation={navigation} user={currentUser} />
      </View>

      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.banner, { backgroundColor: '#032d60' }]}>
          <Text style={styles.bannerTitle}>Adoption Dashboard</Text>
          <Text style={styles.bannerSub}>
            Track missing exercises, media, course content, and stories — fix gaps so students
            stand out with complete training material.
          </Text>
        </View>

        <TouchableOpacity onPress={load} style={styles.refreshBtn} activeOpacity={0.7}>
          <Text style={styles.refreshBtnText}>Refresh data</Text>
        </TouchableOpacity>

        {loading ? (
          <ActivityIndicator size="large" color="#0176d3" style={{ marginVertical: 24 }} />
        ) : null}
        {error ? (
          <View style={[styles.errorBox, { borderColor: '#ba0517' }]}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        ) : null}

        <View style={styles.widgetGrid}>{ADOPTION_WIDGETS.map(renderWidget)}</View>
      </ScrollView>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#dddbda',
  },
  hamburgerContainer: { width: 44 },
  breadcrumbContainer: { flex: 1, paddingHorizontal: 8 },
  breadcrumb: { fontSize: 22, fontWeight: '700' },
  breadcrumbSub: { fontSize: 13, marginTop: 2 },
  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 40 },
  banner: {
    borderRadius: 8,
    padding: 20,
    marginBottom: 16,
  },
  bannerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 8,
  },
  bannerSub: {
    color: 'rgba(255,255,255,0.88)',
    fontSize: 14,
    lineHeight: 20,
  },
  refreshBtn: { alignSelf: 'flex-start', marginBottom: 12 },
  refreshBtnText: { color: '#0176d3', fontSize: 14, fontWeight: '600' },
  errorBox: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  errorText: { color: '#ba0517', fontSize: 14 },
  widgetGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  widgetCard: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 16,
    minHeight: 160,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.08,
          shadowRadius: 3,
          elevation: 2,
        }),
  },
  widgetHead: { marginBottom: 12 },
  widgetTitle: { fontSize: 15, fontWeight: '700', marginBottom: 4 },
  widgetSub: { fontSize: 12, lineHeight: 16, opacity: 0.85 },
  widgetMetric: { fontSize: 36, fontWeight: '700', marginTop: 4 },
  widgetNames: { fontSize: 15, fontWeight: '600', lineHeight: 22, marginTop: 4 },
  widgetLinkHint: { fontSize: 12, fontWeight: '600', marginTop: 12 },
});
